// @givmo/board — a director's unsent words, kept for this tab across a forced sign-out (CBG-SEC2).
//
// "Nothing typed is lost" is the contract's promise, and a session can end under a director's hands:
// redeeming a recovery code inside the "Confirm it's you" sheet signs the account out everywhere, and
// the console then remounts its whole route tree at its account boundary, unmounting every page. So
// the two places a director types (a disclosure, a packet's note or comment) keep a DRAFT here, and
// put it back when the same account returns to the same place.
//
// A disclosure can be sensitive, so the limits are tight (the orchestrator, 2026-10-03 14:13 PDT):
//   - sessionStorage ONLY, never localStorage: the draft lives and dies with this tab;
//   - keyed by the account's uid, the organization, the seat (and the packet, for a note or comment);
//   - only the director's own typed text, never anything the server sent or any fact about the seat,
//     written only for non-empty fields, debounced;
//   - read back only under the SAME account and the same surface: a draft is never shown to another
//     account, and the host purges every other account's drafts at its account boundary BEFORE it
//     renders anything for the new one (`purgeDirectorDraftsNotOwnedBy`);
//   - cleared on a successful submit and on an explicit Cancel or Discard, and dropped on read once
//     it is older than a day (each draft is stamped when its text last changed);
//   - READ BEFORE WRITE: a surface reads what is kept for its scope before it writes anything there,
//     and removes a kept draft only once the director has typed in that scope since reading it. A
//     surface whose scope arrives late (the seat comes from `/me`) can't erase a draft it never put
//     back, in any order of events (review-43 D1).
//
// Every storage access is guarded: a browser that refuses sessionStorage keeps no draft, and the
// pages work exactly as they did.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PREFIX = 'givmo.board.director-draft.v1';

/** How long a draft may wait: a day, the recovery code's own wait before signing reopens. */
export const DIRECTOR_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** How long typing settles before a draft is written. */
export const DIRECTOR_DRAFT_WRITE_DELAY_MS = 300;

/**
 * Where a director types. `coi-questionnaire` (CBG-YR2-WEB2) is the year's annual questionnaire: what
 * the person typed or changed there, never the answers the server filled in from last year.
 * `self-assessment` (CBG-YR4-ASSESS-WEB) is the board's self-assessment: the answers, comments and
 * words the person chose or changed since their last send, never the answers the server sent back.
 */
export type DirectorDraftSurface =
  | 'disclosure'
  | 'packet-note'
  | 'packet-comment'
  | 'coi-questionnaire'
  | 'self-assessment';

/** Whose draft, and where: the account, the board, the seat, the surface (and the packet, or the year). */
export interface DirectorDraftScope {
  uid: string;
  orgRef: string;
  seatId: string;
  surface: DirectorDraftSurface;
  packetId?: string;
  /** The year's cycle, for the annual questionnaire (CBG-YR2-WEB2). */
  cycleId?: string;
  /** The self-assessment, for its answers (CBG-YR4-ASSESS-WEB). */
  assessmentId?: string;
}

/** A draft's typed fields: field name → the text as typed. */
export type DirectorDraftFields = Readonly<Record<string, string>>;

interface StoredDraft {
  savedAt: number;
  fields: Record<string, string>;
}

function storage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function keyOf(scope: DirectorDraftScope): string {
  // The packet's place holds the year's cycle for the questionnaire and the self-assessment's id for
  // its answers, so every older key is unchanged.
  const parts = [
    scope.uid,
    scope.orgRef,
    scope.seatId,
    scope.surface,
    scope.packetId ?? scope.cycleId ?? scope.assessmentId ?? '',
  ];
  return [PREFIX, ...parts.map(encodeURIComponent)].join(':');
}

/** The account a stored key belongs to, or `null` for a key that isn't a director draft's. */
function ownerOf(key: string): string | null {
  if (!key.startsWith(`${PREFIX}:`)) return null;
  const encoded = key.slice(PREFIX.length + 1).split(':')[0];
  if (encoded === undefined) return null;
  try {
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

function parse(raw: string | null): StoredDraft | null {
  if (raw === null) return null;
  try {
    const value = JSON.parse(raw) as unknown;
    if (typeof value !== 'object' || value === null) return null;
    const { savedAt, fields } = value as { savedAt?: unknown; fields?: unknown };
    if (typeof savedAt !== 'number' || typeof fields !== 'object' || fields === null) return null;
    const kept: Record<string, string> = {};
    for (const [name, text] of Object.entries(fields)) {
      if (typeof text === 'string' && text.trim().length > 0) kept[name] = text;
    }
    return { savedAt, fields: kept };
  } catch {
    return null;
  }
}

/**
 * The draft this account left on this surface, or `null`. A draft older than a day is dropped here,
 * and so is one that can't be read.
 */
export function readDirectorDraft(scope: DirectorDraftScope): DirectorDraftFields | null {
  const store = storage();
  if (store === null) return null;
  const key = keyOf(scope);
  try {
    const draft = parse(store.getItem(key));
    if (draft === null || Date.now() - draft.savedAt > DIRECTOR_DRAFT_MAX_AGE_MS) {
      store.removeItem(key);
      return null;
    }
    return Object.keys(draft.fields).length > 0 ? draft.fields : null;
  } catch {
    return null;
  }
}

function sameFields(kept: Record<string, string>, other: Record<string, string>): boolean {
  const names = Object.keys(kept);
  return (
    names.length === Object.keys(other).length && names.every((name) => kept[name] === other[name])
  );
}

/**
 * Keep the non-empty fields as this surface's draft; with none left, the draft goes. The same words
 * keep their stamp: a draft's age runs from when its text last changed, so putting it back on the
 * screen never makes it younger.
 */
export function writeDirectorDraft(scope: DirectorDraftScope, fields: DirectorDraftFields): void {
  const store = storage();
  if (store === null) return;
  const kept: Record<string, string> = {};
  for (const [name, text] of Object.entries(fields)) {
    if (text.trim().length > 0) kept[name] = text;
  }
  const key = keyOf(scope);
  try {
    if (Object.keys(kept).length === 0) {
      store.removeItem(key);
      return;
    }
    const stored = parse(store.getItem(key));
    if (stored !== null && sameFields(kept, stored.fields)) return;
    const draft: StoredDraft = { savedAt: Date.now(), fields: kept };
    store.setItem(key, JSON.stringify(draft));
  } catch {
    // A full or blocked store keeps no draft; the page is unaffected.
  }
}

/** Drop this surface's draft: the words were sent, or the director put them away. */
export function clearDirectorDraft(scope: DirectorDraftScope): void {
  try {
    storage()?.removeItem(keyOf(scope));
  } catch {
    // Nothing to clear in a store that can't be reached.
  }
}

/**
 * The host's account boundary: remove every director draft in this tab that belongs to any account
 * but `uid`. A host calls it as a different account signs in, BEFORE anything renders for that
 * account, so one person's unsent words can never be put in front of another.
 */
export function purgeDirectorDraftsNotOwnedBy(uid: string): void {
  const store = storage();
  if (store === null) return;
  try {
    const foreign: string[] = [];
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (key === null) continue;
      const owner = ownerOf(key);
      if (owner !== null && owner !== uid) foreign.push(key);
    }
    for (const key of foreign) store.removeItem(key);
  } catch {
    // A store that can't be read holds nothing this boundary could leak.
  }
}

/** What a surface does with its draft once the words are sent or put away. */
export interface DirectorDraftWriter {
  /**
   * Drop the draft and any write still waiting: the words were sent, or the director cancelled.
   * Nothing is kept again until the text changes.
   */
  discard: () => void;
}

function hasText(fields: DirectorDraftFields): boolean {
  return Object.values(fields).some((text) => text.trim().length > 0);
}

/** One read of a scope's draft: a fresh object each time, so "typed since" belongs to one read. */
interface DraftRead {
  key: string;
}

/**
 * This surface's draft: read back as its scope becomes known, then kept while the director types.
 * `scope` null keeps nothing.
 *
 * READ BEFORE WRITE (see the header). In the render where a scope becomes known, what is kept for it
 * is read and handed to `restore`, which puts the words back where nothing has been typed yet; the
 * render is redone before it commits, so the writer first sees the fields as restored. A kept draft
 * is removed only once the director has typed in that scope since the read.
 *
 * Written once typing settles, and never after the surface is gone: a write still waiting when the
 * surface unmounts is cancelled, so nothing is written for an account that has already left. Typing
 * in the last `DIRECTOR_DRAFT_WRITE_DELAY_MS` before a page goes is not kept, by design.
 */
export function useDirectorDraft(
  scope: DirectorDraftScope | null,
  fields: DirectorDraftFields,
  restore: (kept: DirectorDraftFields) => void,
): DirectorDraftWriter {
  const key = scope === null ? null : keyOf(scope);
  // The read this surface last made. Set during render, React's pattern for state that follows a
  // prop: the render is redone at once with it, and with whatever `restore` set.
  const [read, setRead] = useState<DraftRead | null>(null);
  if (scope !== null && key !== null && read?.key !== key) {
    setRead({ key });
    const kept = readDirectorDraft(scope);
    if (kept !== null) restore(kept);
  }
  // `fields` is a fresh object each render; its JSON is what changes when the director types.
  const serialized = JSON.stringify(fields);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The read the fields have held text under: only there may emptied fields remove the draft.
  const typedSince = useRef<DraftRead | null>(null);
  // The text as it stood when the draft was discarded: nothing is written until it changes.
  const discardedAt = useRef<string | null>(null);
  const latest = useRef(serialized);
  latest.current = serialized;

  useEffect(() => {
    const current = scopeRef.current;
    // Nothing is written for a scope before it is read.
    if (key === null || current === null || read === null || read.key !== key) return;
    const typed = JSON.parse(serialized) as DirectorDraftFields;
    // Empty fields remove a kept draft only if they held text since the read: a surface never
    // erases words it didn't put back.
    if (hasText(typed)) typedSince.current = read;
    else if (typedSince.current !== read) return;
    if (discardedAt.current !== null) {
      if (discardedAt.current === serialized) return;
      discardedAt.current = null;
    }
    timer.current = setTimeout(() => {
      timer.current = null;
      writeDirectorDraft(current, typed);
    }, DIRECTOR_DRAFT_WRITE_DELAY_MS);
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [key, serialized, read]);

  const discard = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    discardedAt.current = latest.current;
    if (scopeRef.current !== null) clearDirectorDraft(scopeRef.current);
  }, []);

  return useMemo(() => ({ discard }), [discard]);
}
